import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import {
  UserProfileData,
  getUserPrimaryRole,
  hasElevatedPermissions,
  getUserType,
  UserType,
  canAccessDashboard,
  canAccessAccounts,
  canAccessUsers,
  canAccessContentManagement,
  canAccessTemplates,
  canReviewContent,
  canAccessCourses,
  canAccessSettings,
  canAccessAiRecommendation,
  canAccessAiRecommendationAsAdmin,
  canAccessAiRecommendationAsSalesOps,
  canAccessTrainingPlanGateway,
} from '../types/UserProfile';
import { fetchUserProfile } from '../api/userProfileApi';
import { useUser } from '../components/OidcProvider/OidcProvider';
import { initializePendo } from '../utils/pendo';
import AppConfig from '../AppConfig';

interface UserProfileContextValue {
  profileData: UserProfileData | null;
  isLoading: boolean;
  error: string | null;
  hasElevatedPermissions: boolean;
  userRole: string | null;
  userType: UserType; // Access control: super_admin, standard, account_admin, content_manager
  appAccountType: 'compliance_education' | 'legacy' | 'trial'; // UI customization
  isOrganizationSuspended: boolean; // Organization suspension status
  // Route access flags
  canAccessDashboard: boolean;
  canAccessAccounts: boolean;
  canAccessUsers: boolean;
  canAccessContentManagement: boolean;
  canAccessTemplates: boolean;
  canReviewContent: boolean;
  canAccessCourses: boolean;
  canAccessSettings: boolean;
  canAccessAiRecommendation: boolean;
  canAccessAiRecommendationAsAdmin: boolean;
  canAccessAiRecommendationAsSalesOps: boolean;
  canAccessTrainingPlanGateway: boolean;
  refetch: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined);

interface UserProfileProviderProps {
  children: ReactNode;
}

export function UserProfileProvider({ children }: UserProfileProviderProps) {
  const { userProfile: oidcProfile, isLoading: isOidcLoading } = useUser();
  const requestIdRef = useRef(0);
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as true to prevent premature access denial
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    if (!oidcProfile) {
      setProfileData(null);
      setIsLoading(false);
      return;
    }

    const currentRequest = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchUserProfile();
      if (currentRequest !== requestIdRef.current) {
        return;
      }

      if (response.success) {
        setProfileData(response.data);
      } else {
        throw new Error('Profile fetch returned unsuccessful response');
      }
    } catch (err) {
      if (currentRequest !== requestIdRef.current) {
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to load user profile';
      setError(errorMessage);
      setProfileData(null);
    } finally {
      if (currentRequest === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [oidcProfile]);

  // Load profile when user logs in
  useEffect(() => {
    // Wait for OIDC to finish loading first
    if (isOidcLoading) {
      setIsLoading(true);
      return;
    }

    if (oidcProfile) {
      void loadProfile();
    } else {
      requestIdRef.current += 1;
      setProfileData(null);
      setIsLoading(false); // Set loading to false when no OIDC profile
    }
  }, [oidcProfile, isOidcLoading, loadProfile]);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  console.log('[UserProfileContext] Profile data loaded', { profileData, error });

  // Initialize Pendo after successful profile load
  useEffect(() => {
    if (!profileData || isLoading) {
      return;
    }

    // Initialize Pendo with user profile data
    try {
      if (typeof window !== 'undefined') {
        initializePendo(
          profileData.userId,
          profileData.email,
          profileData.displayName || undefined,
          getUserPrimaryRole(profileData) || undefined,
          getUserType(profileData),
          profileData.organization?.id,
          profileData.organization?.name,
          profileData.organization?.appAccountType,
          profileData.organization?.status,
        );
      }
    } catch (error) {
      console.error('[UserProfileContext] Failed to initialize Pendo:', error);
    }
  }, [profileData, isLoading]);

  // Derive userType from profileData.userType for access control
  const userType = useMemo(() => getUserType(profileData), [profileData]);

  // Derive appAccountType from profileData.organization.appAccountType for UI customization
  // If appAccountType is 'legacy', user should not see customized content sections
  // If appAccountType is 'compliance_education', user should see customized content sections
  const appAccountType = useMemo(() => {
    if (!profileData?.organization?.appAccountType) {
      // Default to 'legacy' if data is not available yet
      if (profileData && !profileData.organization) {
        console.warn('[UserProfileContext] Missing organization data in profile response', { profileData });
      } else if (profileData?.organization && !profileData.organization.appAccountType) {
        console.warn('[UserProfileContext] Missing appAccountType in organization data', {
          organization: profileData.organization,
        });
      }
      return 'legacy';
    }
    return profileData.organization.appAccountType;
  }, [profileData]);

  // Check if organization is suspended
  const isOrganizationSuspended = useMemo(() => {
    const status = profileData?.organization?.status?.toLowerCase();
    const isSuspended = status === 'suspended';

    if (isSuspended) {
      console.warn('[UserProfileContext] Organization is suspended', {
        organizationId: profileData?.organization?.id,
        organizationName: profileData?.organization?.name,
        status: profileData?.organization?.status,
      });
    }

    return isSuspended;
  }, [profileData]);

  const value: UserProfileContextValue = {
    profileData,
    isLoading,
    error,
    hasElevatedPermissions: hasElevatedPermissions(profileData),
    userRole: getUserPrimaryRole(profileData),
    userType, // Access control based on API userType
    appAccountType, // UI customization based on organization.appAccountType
    isOrganizationSuspended, // Organization suspension status
    // Route access flags
    canAccessDashboard: canAccessDashboard(profileData),
    canAccessAccounts: canAccessAccounts(profileData),
    canAccessUsers: canAccessUsers(profileData),
    canAccessContentManagement: canAccessContentManagement(profileData),
    canAccessTemplates: canAccessTemplates(profileData, AppConfig.contentAuthoringEnabled),
    canReviewContent: canReviewContent(profileData),
    canAccessCourses: canAccessCourses(profileData),
    canAccessSettings: canAccessSettings(profileData),
    canAccessAiRecommendation: canAccessAiRecommendation(profileData, {
      internalDevToolsEnabled: AppConfig.internalDevToolsEnabled,
      salesOpsAiRecommendationEnabled: AppConfig.salesOpsAiRecommendationEnabled,
    }),
    canAccessAiRecommendationAsAdmin: canAccessAiRecommendationAsAdmin(
      profileData,
      AppConfig.internalDevToolsEnabled,
    ),
    canAccessAiRecommendationAsSalesOps: canAccessAiRecommendationAsSalesOps(
      profileData,
      AppConfig.salesOpsAiRecommendationEnabled,
    ),
    canAccessTrainingPlanGateway: canAccessTrainingPlanGateway(profileData, {
      internalDevToolsEnabled: AppConfig.internalDevToolsEnabled,
      salesOpsAiRecommendationEnabled: AppConfig.salesOpsAiRecommendationEnabled,
    }),
    refetch: loadProfile,
  };

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

export function useUserProfile(): UserProfileContextValue {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
}
